import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectorRef } from "@angular/core";
import { LSICalculationRequestModel, StructureModel, OrendaChemicalHistoryModel, DosageRecommendationRequestModel, DosageRecommendationResponse, IDropdownModel, DropdownModel } from "@models";
import { OrendaService, GlobalsService } from "@services";
import { OrendaChemicalHistoryComponent } from "./orenda-chemical-history.component";
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
    selector: "orenda",
    templateUrl: "./orenda.component.html",
    styleUrls: ['orenda.component.scss'],
    providers: []
})

export class OrendaComponent implements OnChanges, OnInit {
    @Input() jobId!: number;
    @Input() jobDocumentKey!: string;
    @Input() customerId!: number;
    // The pool/spa being worked on. Owned by the Assets tab, which is where structures are added and
    // edited - this component only reads the volume it needs to dose against.
    @Input() structure!: StructureModel;

    @ViewChild(OrendaChemicalHistoryComponent) chemicalHistoryComponent!: OrendaChemicalHistoryComponent;
    firstItem: OrendaChemicalHistoryModel;

    lsi: LSICalculationRequestModel = new LSICalculationRequestModel();
    result: { lsiCurrent: number; lsiDesired: number; }
    private recalcSubject = new Subject<void>();

    dosageResponse: DosageRecommendationResponse | null = null;
    lsiValue: number = 0;
    lsiDesired: number = 0;
    poolGallons: number | null = null;
    poolLength: number | null = null;
    poolWidth: number | null = null;
    poolDepth: number | null = null;
    calculatedGallons: number | null = null;
    poolDiameter: number | null = null;
    poolDeepDepth: number | null = null;
    poolShallowDepth: number | null = null;
    isCalculating: boolean = false;
    hasChemicalHistory: boolean = false;


    dosageKeys(): string[] {
        return this.dosageResponse ? Object.keys(this.dosageResponse.actions) : [];
    }

    maintenanceKeys(): string[] {
        return this.dosageResponse && this.dosageResponse.maintenance
            ? Object.keys(this.dosageResponse.maintenance)
            : [];
    }

    dosageJsonString: string;

    chemicalFields = [
        { label: 'Water °F', current: 'waterTemperatureCurrentF', desired: 'waterTemperatureDesiredF' },
        { label: 'PH', current: 'phCurrent', desired: 'phDesired' },
        { label: 'Total Alkalinity', current: 'totalAlkalinityCurrent', desired: 'totalAlkalinityDesired' },
        { label: 'Calcium', current: 'calciumCurrent', desired: 'calciumDesired' },
        { label: 'CYA', current: 'cyaCurrent', desired: 'cyaDesired' },
        { label: 'TDS/Salt', current: 'saltCurrent', desired: 'saltDesired' },
        { label: 'Borates', current: 'boratesCurrent', desired: 'boratesDesired' },
        { label: 'Chlorine', current: 'chlorineCurrent', desired: 'chlorineDesired' },
        { label: 'Phosphate', current: 'phosphateCurrent', desired: 'phosphateDesired' },
    ];

    chemicalFieldsLSI = this.chemicalFields.slice(0, 7);  // These are all things that affect the LSI
    chemicalFieldsExtras = this.chemicalFields.slice(7); // These are extra things

    chemicalFieldsConfig: { [key: string]: { min: number; max: number; default: number } } = {
        boratesCurrent: { min: 0, max: 100, default: 50 },
        boratesDesired: { min: 0, max: 100, default: 50 },
        calciumCurrent: { min: 10, max: 2000, default: 300 },
        calciumDesired: { min: 10, max: 2000, default: 300 },
        chlorineCurrent: { min: 0, max: 25, default: 0.0 },
        chlorineDesired: { min: 0, max: 25, default: 6.0 },
        cyaCurrent: { min: 0, max: 600, default: 0 },
        cyaDesired: { min: 0, max: 600, default: 0 },
        phCurrent: { min: 4.0, max: 10.5, default: 7.6 },
        phDesired: { min: 4.0, max: 10.5, default: 7.6 },
        phosphateCurrent: { min: 0, max: 10000, default: 500 },
        phosphateDesired: { min: 0, max: 10000, default: 1700 },
        saltCurrent: { min: 0, max: 9500, default: 400 },
        saltDesired: { min: 0, max: 9500, default: 400 },
        totalAlkalinityCurrent: { min: 0, max: 400, default: 80 },
        totalAlkalinityDesired: { min: 0, max: 400, default: 80 },
        waterTemperatureCurrentF: { min: 32, max: 212, default: 80 },
        waterTemperatureDesiredF: { min: 32, max: 212, default: 80 }
    };


    brands: IDropdownModel[] = [
        new DropdownModel("AquaStar", "AquaStar"),
        new DropdownModel("Hayward", "Hayward"),
        new DropdownModel("Jandy", "Jandy"),
        new DropdownModel("Pentair", "Pentair"),
        new DropdownModel("Raypak", "Raypak"),
        new DropdownModel("Other", "Other")
    ]


    constructor(private orendaService: OrendaService,
        private cdr: ChangeDetectorRef) { }

    async ngOnInit(): Promise<void> {
        if (!this.lsi) {
        this.lsi = new LSICalculationRequestModel();
        }

        this.chemicalFields.forEach(field => {
        const currentKey = field.current;
        const desiredKey = field.desired;

        if (this.lsi[currentKey] === undefined || this.lsi[currentKey] === null) {
            this.lsi[currentKey] = this.chemicalFieldsConfig[currentKey]?.default ?? this.chemicalFieldsConfig[currentKey].min ?? 0;
        }

        if (this.lsi[desiredKey] === undefined || this.lsi[desiredKey] === null) {
            this.lsi[desiredKey] = this.chemicalFieldsConfig[desiredKey]?.default ?? this.chemicalFieldsConfig[desiredKey].min ?? 0;
        }
        });
        this.recalcSubject.pipe(
            debounceTime(300) // wait until typing/scrolling settles
        ).subscribe(() => {
            this.recalcLSI();
        });

        // trigger initial calculation
        this.recalcSubject.next();
    }

    async ngOnChanges(changes: SimpleChanges) {
        // Switching pools throws away the last calculation - the dosages were worked out against the
        // previous structure's volume, so showing them next to a different pool would be wrong.
        if (changes.structure) {
            this.result = null;
            this.dosageResponse = null;

            if (this.chemicalHistoryComponent) {
                await this.chemicalHistoryComponent.loadChemicalHistory();
                this.populateDesiredFields();
                this.cdr.detectChanges();
            }
        }
    }

    async ngAfterViewInit(): Promise<void> {
        if (this.chemicalHistoryComponent && this.structure) {
            await this.chemicalHistoryComponent.loadChemicalHistory();

            this.populateDesiredFields();

            this.cdr.detectChanges();
        }
    }

    get structureId(): number {
        return this.structure?.id ?? 0;
    }

    populateDesiredFields(): void {
        this.firstItem = this.chemicalHistoryComponent.getFirstChemicalHistoryItem(this.structureId);
        this.hasChemicalHistory = !!this.firstItem;
        if (this.firstItem) {
            // Map OrendaChemicalHistoryModel desired fields to LSICalculationRequestModel
            this.lsi.boratesDesired = this.firstItem.boratesDesired ?? this.lsi.boratesDesired;
            this.lsi.calciumDesired = this.firstItem.calciumDesired ?? this.lsi.calciumDesired;
            this.lsi.chlorineDesired = this.firstItem.chlorineDesired ?? this.lsi.chlorineDesired;
            this.lsi.cyaDesired = this.firstItem.cyaDesired ?? this.lsi.cyaDesired;
            this.lsi.phDesired = this.firstItem.phDesired ?? this.lsi.phDesired;
            this.lsi.phosphateDesired = this.firstItem.phosphateDesired ?? this.lsi.phosphateDesired;
            this.lsi.saltDesired = this.firstItem.saltDesired ?? this.lsi.saltDesired;
            this.lsi.totalAlkalinityDesired = this.firstItem.totalAlkalinityDesired ?? this.lsi.totalAlkalinityDesired;
            this.lsi.waterTemperatureDesiredF = this.firstItem.waterTemperatureDesiredF ?? this.lsi.waterTemperatureDesiredF;
            this.recalcSubject.next();
        }
    }

    getStep(fieldKey: string): number {
        if (fieldKey.toLowerCase().includes('borates')) return 1;
        if (fieldKey.toLowerCase().includes('calcium')) return 10;
        if (fieldKey.toLowerCase().includes('chlorine')) return 0.25;
        if (fieldKey.toLowerCase().includes('cya')) return 5;
        if (fieldKey.toLowerCase().includes('phosphate')) return 100;
        if (fieldKey.toLowerCase().includes('ph')) return 0.1;
        if (fieldKey.toLowerCase().includes('salt')) return 100;
        if (fieldKey.toLowerCase().includes('totalAlkalinity')) return 5;
        if (fieldKey.toLowerCase().includes('waterTemperature')) return 1;
        return 1;
    }

    async calculateLSI(): Promise<void>  {
        try {
            this.result = await this.fetchLSI();
            this.result.lsiCurrent = Math.round(this.result.lsiCurrent * 100) / 100;
            this.result.lsiDesired = Math.round(this.result.lsiDesired * 100) / 100;
            await this.getDosageRecommendations();

        } catch (error) {
            console.error('Error calculating LSI', error);
        }
    }

    async getDosageRecommendations() {

        const dosageRequest = new DosageRecommendationRequestModel({
            gallons: this.structure?.volume ?? 0,
            boratesCurrent: this.lsi.boratesCurrent,
            boratesDesired: this.lsi.boratesDesired,
            calciumCurrent: this.lsi.calciumCurrent,
            calciumDesired: this.lsi.calciumDesired,
            chlorineCurrent: this.lsi.chlorineCurrent,
            chlorineDesired: this.lsi.chlorineDesired,
            cyaCurrent: this.lsi.cyaCurrent,
            cyaDesired: this.lsi.cyaDesired,
            phCurrent: this.lsi.phCurrent,
            phDesired: this.lsi.phDesired,
            phosphateCurrent: this.lsi.phosphateCurrent,
            phosphateDesired: this.lsi.phosphateDesired,
            saltCurrent: this.lsi.saltCurrent,
            saltDesired: this.lsi.saltDesired,
            totalAlkalinityCurrent: this.lsi.totalAlkalinityCurrent,
            totalAlkalinityDesired: this.lsi.totalAlkalinityDesired,
            waterTemperatureCurrentF: this.lsi.waterTemperatureCurrentF,
            waterTemperatureDesiredF: this.lsi.waterTemperatureDesiredF,
        });

        try {
            this.dosageResponse = await this.orendaService.getDosageRecommendationService(dosageRequest);
            this.dosageJsonString = JSON.stringify(this.dosageResponse);
            if (this.dosageResponse?.maintenance) {
                for (const productName in this.dosageResponse.maintenance) {
                    const entry = this.dosageResponse.maintenance[productName];
                    for (const key in entry) {
                        const item = entry[key];
                        if (item?.quantity !== undefined && !isNaN(item.quantity)) {
                            item.quantity = Math.round(item.quantity * 100) / 100;
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load dosages', error);
        }
    }

    async addOrendaChemicalHistory(): Promise<void> {
        const chemicalHistory = OrendaChemicalHistoryModel.fromLSI(
            this.lsi,
            this.jobId,
            this.customerId,
            this.structure.id,
            this.result.lsiCurrent,
            this.result.lsiDesired,
            this.dosageJsonString,
            GlobalsService.userInfo.userId,
            {
                gallons: this.structure?.volume ?? 0,
                volume: this.structure?.volume ?? 0,
                description: this.structure?.description ?? "",
                measurement: "gallons"
            }
        );

        await this.orendaService.addOrendaChemicalHistory(chemicalHistory);

        if (this.chemicalHistoryComponent) {
            this.chemicalHistoryComponent.loadChemicalHistory();
        }
    }

    async saveDesiredSettings(): Promise<void> {
        if (!this.structure?.id)
            return;

        const model = OrendaChemicalHistoryModel.fromLSI(
            this.lsi,
            this.jobId,
            this.customerId,
            this.structure.id,
            this.lsiValue,
            this.lsiDesired,
            null,
            GlobalsService.userInfo.userId,
            {
                gallons: this.structure?.volume ?? 0,
                volume: this.structure?.volume ?? 0,
                description: this.structure?.description ?? "",
                measurement: "gallons"
            }
        );

        try {
            await this.orendaService.updateOrendaDesiredSettings(
                this.customerId,
                this.jobId,
                this.structure.id,
                model
            );

            await this.chemicalHistoryComponent.loadChemicalHistory();
            this.populateDesiredFields();
            this.cdr.detectChanges();

            await Swal.fire({
                icon: 'success',
                title: 'Saved',
                text: 'Desired settings saved successfully.',
                timer: 1500,
                showConfirmButton: false
            });
        }
        catch (error) {
            console.error('Failed to save desired settings', error);

            await Swal.fire({
                icon: 'error',
                title: 'Unable to save',
                text: 'Desired settings could not be saved.'
            });
        }
    }

     getDisplayName(productName: string): string {
        if (!productName) return '';

        const normalized = productName.toLowerCase();

        const map: Record<string, string> = {
            'pr-10': 'Phosphate Remover',
            'sc-1000': 'Scale and Metal Control',
            'cv-600': 'Enzymes',
            'cv-700': 'Enzymes + Phosphate Remover',
            'ce-clarifier': 'Chitosan Clarifier + Enzymes',
            'spa-500': 'Pre-Drain Spa Scrub',
            'ce-spa': 'Chitosan Clarifier + Enzymes'
        };

        for (const key in map) {
            if (normalized.includes(key)) {
                // return “SC-1000 (Scale and Metal Control)”
                return `${productName} (${map[key]})`;
            }
        }

        return productName;
    }

    areAllInputsFilled(): boolean {
        return this.chemicalFields.every(field =>
            this.lsi[field.current] != null &&
            this.lsi[field.desired] != null &&
            this.lsi[field.current] !== '' &&
            this.lsi[field.desired] !== ''
        );
    }

    private async fetchLSI(): Promise<any> {
        const url = `/orenda/CalculateLSI?` +
            `boratesCurrent=${this.lsi.boratesCurrent}&boratesDesired=${this.lsi.boratesDesired}&` +
            `calciumCurrent=${this.lsi.calciumCurrent}&calciumDesired=${this.lsi.calciumDesired}&` +
            `chlorineCurrent=${this.lsi.chlorineCurrent}&chlorineDesired=${this.lsi.chlorineDesired}&` +
            `cyaCurrent=${this.lsi.cyaCurrent}&cyaDesired=${this.lsi.cyaDesired}&` +
            `phCurrent=${this.lsi.phCurrent}&phDesired=${this.lsi.phDesired}&` +
            `phosphateCurrent=${this.lsi.phosphateCurrent}&phosphateDesired=${this.lsi.phosphateDesired}&` +
            `saltCurrent=${this.lsi.saltCurrent}&saltDesired=${this.lsi.saltDesired}&` +
            `totalAlkalinityCurrent=${this.lsi.totalAlkalinityCurrent}&totalAlkalinityDesired=${this.lsi.totalAlkalinityDesired}&` +
            `waterTemperatureCurrentF=${this.lsi.waterTemperatureCurrentF}&waterTemperatureDesiredF=${this.lsi.waterTemperatureDesiredF}`;

        return this.orendaService.getCalculateLSI(url);
    }

    async recalcLSI(): Promise<void> {
        try {
            const result = await this.fetchLSI();
            // Save only the current LSI value
            this.lsiValue = Math.round(result.lsiCurrent * 100) / 100;
            this.lsiDesired = Math.round(result.lsiDesired * 100) / 100;
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error recalculating LSI', error);
        }
    }

    getLsiClass(value: number): string {
        if (value >= 0.3) {
            return 'lsi-purple';
        } else if (value >= 0 && value < 0.3) {
            return 'lsi-green';
        } else if (value >= -0.3 && value < 0) {
            return 'lsi-yellow';
        } else {
            return 'lsi-red';
        }
    }

    allowOnlyNumbers(event: KeyboardEvent): void {
        const allowedKeys = [
            'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete',
            'Home', 'End', 'Enter'
        ];

        if (
            allowedKeys.includes(event.key) ||
            // Allow Ctrl/Cmd + key combos (e.g., Ctrl+C)
            (event.ctrlKey || event.metaKey)
        ) {
            return;
        }

        // Block non-numeric input
        if (!/^\d$/.test(event.key) && event.key !== '.') {
            event.preventDefault();
        }
    }
}
